import { AnimationType, GameConstants, Layer, ObjectCategory, PlayerActions, RotationMode } from "../constants";
import { type OctoArmData, type OctoArmState, type OctoData, OctoRig } from "../definitions/octo";
import { Armors, type ArmorDefinition } from "../definitions/items/armors";
import { Backpacks, type BackpackDefinition } from "../definitions/items/backpacks";
import { Buildings, type BuildingDefinition } from "../definitions/buildings";
import { Decals, type DecalDefinition } from "../definitions/decals";
import { GravityBeamMode, type GravityBeamData } from "../definitions/gravity";
import { type HealingItemDefinition } from "../definitions/items/healingItems";
import { Loots, type LootDefinition, type WeaponDefinition } from "../definitions/loots";
import { type MeleeDefinition, Melees } from "../definitions/items/melees";
import { Obstacles, type ObstacleDefinition } from "../definitions/obstacles";
import { Skins, type SkinDefinition } from "../definitions/items/skins";
import { type Emblem, readEmblem, writeEmblem } from "./emblem";
import { heightFromByte, heightToByte } from "./jumpIn";
import { heightByte as animalHeightByte, heightFromByte as animalHeightFromByte } from "../definitions/animals";
import { SyncedParticles, type SyncedParticleDefinition } from "../definitions/syncedParticles";
import { Throwables, type ThrowableDefinition } from "../definitions/items/throwables";
import { type Orientation, type Variation } from "../typings";
import { Angle, halfπ, Numeric } from "./math";
import { type Mutable, type SDeepMutable } from "./misc";
import { type TrenchioByteStream } from "./trenchioByteStream";
import { type Vector } from "./vector";

/** a death marker's age is sent in steps of this (ms), up to `DEATH_AGE_STEPS` of them (four minutes and more: 1.06.1) */
export const DEATH_AGE_STEP = 4000;
export const DEATH_AGE_STEPS = 0x3f;

export interface Fullable<T> {
    full?: T
}

type BaseObjectsNetData = Record<ObjectCategory, Fullable<object> | object>;

export type FullData<Cat extends ObjectCategory> = ObjectsNetData[Cat] & (ObjectsNetData[Cat] extends Fullable<infer S> ? { full: S } : object);

type FullDeserializationType<T extends ObjectCategory> = ObjectsNetData[T] extends { full?: infer Full } ? NonNullable<Full> : undefined;

export interface ObjectsNetData extends BaseObjectsNetData {
    //
    // Player Data
    //
    readonly [ObjectCategory.Player]: {
        readonly position: Vector
        readonly rotation: number
        readonly animation?: AnimationType
        readonly action?: ({
            readonly type: Exclude<PlayerActions, PlayerActions.UseItem>
            readonly item?: undefined
        } | {
            readonly type: PlayerActions.UseItem
            readonly item: HealingItemDefinition
        })
        /** the Gravity Gloves' beam, while there is one (definitions/gravity.ts) */
        readonly beam?: GravityBeamData
        /** the player's flashlight is on (1.05: it shows at night, see definitions/night.ts) */
        readonly flashlight?: boolean
        /**
         * in the air (1.06, jumping in: definitions/jumpIn.ts): how high (m), and whether still in a plane (only the player
         * themselves, and whoever watches them, is ever sent one in a plane)
         */
        /**
         * the air's `tossed` (0.98.4): spun in a tornado (definitions/storm.ts), neither in a plane nor under a chute; the
         * page draws them lifted and turning. Sent as the plane's and the brake's bits together, which the jump-in never
         * sets (nobody brakes in a plane)
         */
        readonly air?: {
            readonly height: number
            readonly aboard: boolean
            /** the chute braked: the jump key held (1.09), the canopy flared */
            readonly braking?: boolean
            /** spun in a tornado (0.98.4): lifted and turning, for the storm's 5 seconds */
            readonly tossed?: boolean
        }
        /** climbing a ladder or through a hatch (1.09, the roofs' passages): rooted there for the moment, drawn climbing */
        readonly climbing?: boolean
        /** the Octo Gadget's four arms, while the player carries it (1.10, definitions/octo.ts): drawn alive on their back */
        readonly octo?: OctoData
        readonly full?: {
            readonly layer: Layer
            readonly dead: boolean
            readonly downed: boolean
            readonly beingRevived: boolean
            readonly teamID: number
            readonly invulnerable: boolean
            readonly activeItem: WeaponDefinition
            readonly sizeMod: number
            readonly reloadMod?: number
            readonly skin: SkinDefinition
            readonly helmet?: ArmorDefinition
            readonly vest?: ArmorDefinition
            readonly mask?: ArmorDefinition
            readonly backpack: BackpackDefinition
            readonly halloweenThrowableSkin: boolean
            readonly activeDisguise?: ObstacleDefinition
            readonly infected: boolean
            readonly backEquippedMelee?: MeleeDefinition
            readonly hasBubble: boolean
            readonly activeOverdrive: boolean
            readonly hasMagneticField: boolean
            readonly isCycling: boolean
            readonly emitLowHealthParticles: boolean
            readonly emblem?: Emblem
            /** kills this match: shown as chevrons on the sleeves */
            readonly kills: number
            /** the store's picture emblem the player wears instead of a drawn one (its item id, 0.92) */
            readonly storeEmblem?: string
        }
    }
    //
    // Obstacle Data
    //
    readonly [ObjectCategory.Obstacle]: {
        readonly scale: number
        readonly dead: boolean
        readonly playMaterialDestroyedSound: boolean
        readonly waterOverlay: boolean
        readonly powered: boolean
        /** a wall being built right now (the browser shows the planks filling in) */
        readonly building?: boolean
        /** 0 intact, 1 cracked, 2 badly cracked, 3 about to break (walls show cracks) */
        readonly damageStage?: number
        /**
         * moved or merged by the Gravity Gloves (definitions/gravity.ts): where it is now, how far it has tumbled
         * from its usual angle while flying, and the merged group it belongs to (0 for none)
         */
        readonly dynamic?: {
            readonly position: Vector
            readonly spin: number
            readonly bond: number
        }
        readonly full?: {
            readonly definition: ObstacleDefinition
            readonly position: Vector
            readonly layer: Layer
            readonly rotation: {
                readonly orientation: Orientation
                readonly rotation: number
            }
            readonly activated?: boolean
            readonly door?: {
                readonly offset: number
                readonly locked: boolean
            }
        }
    }
    //
    // Loot Data
    //
    readonly [ObjectCategory.Loot]: {
        readonly position: Vector
        readonly layer: Layer
        readonly full?: {
            readonly definition: LootDefinition
            readonly count: number
            readonly isNew: boolean
        }
    }
    //
    // DeathMarker Data
    //
    readonly [ObjectCategory.DeathMarker]: {
        readonly position: Vector
        readonly layer: Layer
        readonly playerID: number
        readonly isNew: boolean
        /**
         * the body bleeds out into a pool that gives prints (1.06.1, the blood trails: as the server counts one, so the
         * page draws the pools there are, and only those: `BloodTrail.bodyPool`), and how long ago it died (ms, in steps
         * of `DEATH_AGE_STEP`, as its data was last written: every few seconds, so whoever sees it first sees it as old as
         * it is)
         */
        readonly pool?: boolean
        readonly age?: number
    }
    //
    // Building Data
    //
    readonly [ObjectCategory.Building]: {
        readonly dead: boolean
        readonly puzzle: undefined | {
            readonly solved: boolean
            readonly errorSeq: boolean
        }
        readonly layer: number
        /** its roof's panes smashed (1.08, `BuildingDefinition.glass`): a bit each, the first pane's the lowest */
        readonly brokenGlass: number
        /** its roof (0.98.4, definitions/storm.ts `RoofState`): whole, or torn off by a tornado */
        readonly roof?: number
        readonly full?: {
            readonly definition: BuildingDefinition
            readonly position: Vector
            readonly orientation: Orientation
        }
    }
    //
    // Decal Data
    //
    readonly [ObjectCategory.Decal]: {
        readonly position: Vector
        readonly rotation: number
        readonly layer: Layer
        readonly definition: DecalDefinition
        /**
         * a blood trail's mark (1.06, `definition.trail`): how old it is (seconds, as its data was last written: the
         * server writes it again every second, so whoever sees it first sees it as dry as it is) and how strong (a print
         * from boots the blood is wearing off: fainter)
         */
        readonly trail?: {
            readonly age: number
            readonly strength: number
        }
    }
    //
    // Parachute data
    //
    readonly [ObjectCategory.Parachute]: {
        readonly height: number
        readonly full?: {
            readonly position: Vector
        }
    }
    //
    // Projectile data
    //
    readonly [ObjectCategory.Projectile]: {
        readonly position: Vector
        readonly rotation: number
        readonly layer: Layer
        readonly height: number

        readonly full?: {
            readonly definition: ThrowableDefinition
            readonly halloweenSkin: boolean
            readonly activated: boolean

            readonly c4?: {
                readonly throwerTeamID: number
                readonly tintIndex: number
            }
        }
    }
    //
    // Synced particle data
    //
    readonly [ObjectCategory.SyncedParticle]: {
        readonly definition: SyncedParticleDefinition
        readonly startPosition: Vector
        readonly endPosition: Vector
        readonly layer: Layer
        readonly age: number
        readonly lifetime?: number
        readonly angularVelocity?: number
        readonly scale?: {
            start: number
            end: number
        }
        readonly alpha?: {
            start: number
            end: number
        }
        readonly variant?: Variation
        readonly creatorID?: number
    }
    //
    // Animal Data (the wild hare, the attic's spiders): everything travels in the partial update
    //
    readonly [ObjectCategory.Animal]: {
        readonly position: Vector
        readonly rotation: number
        readonly layer: Layer
        readonly state: number
        /** which animal it is (`AnimalKind`, common/src/definitions/animals.ts) */
        readonly kind: number
        /** a flier's height in the air (0.98.4, the Mothman): 0 on the ground or a roof, 1 at the top of its flight */
        readonly height: number
    }
    //
    // Fire Data: one chunk of 8 x 8 fire cells; each mask is 64 bits (one per cell) as [low, high] 32-bit words
    //
    readonly [ObjectCategory.Fire]: {
        readonly chunkX: number
        readonly chunkY: number
        readonly burning: FireMask
        readonly kindling: FireMask
        readonly fuel: FireMask
        readonly scorched: FireMask
    }
}

/** 64 cells as two 32-bit words (lo: cells 0-31, hi: cells 32-63); cell bit = row * 8 + column */
export interface FireMask {
    readonly lo: number
    readonly hi: number
}

interface ObjectSerialization<T extends ObjectCategory> {
    serializePartial: (stream: TrenchioByteStream, data: ObjectsNetData[T]) => void
    serializeFull: (stream: TrenchioByteStream, data: FullData<T>) => void
    deserializePartial: (stream: TrenchioByteStream) => ObjectsNetData[T]
    deserializeFull: (stream: TrenchioByteStream) => FullDeserializationType<T>
}

export const ObjectSerializations: { [K in ObjectCategory]: ObjectSerialization<K> } = {
    //
    // Player serialization
    //
    [ObjectCategory.Player]: {
        serializePartial(stream, { position, rotation, animation, action, beam, flashlight, air, climbing, octo }): void {
            stream.writePosition(position);
            stream.writeRotation2(rotation);

            /*
                4 bits for animation, 2 for action
                each has a "dirty" bit, and that makes 8

                our setup will be
                Nnnn nccC

                N: animation dirty
                n: animation
                c: action
                C: action dirty
            */

            const animationDirty = animation !== undefined;
            const actionDirty = action !== undefined;

            let actAnim = (animationDirty ? 128 : 0) + (actionDirty ? 1 : 0);
            if (animationDirty) {
                actAnim += animation << 3;
            }

            if (actionDirty) {
                actAnim += action.type << 1;
            }

            stream.writeUint8(actAnim);

            if (actionDirty && action.item !== undefined) {
                Loots.writeToStream(stream, action.item);
            }

            // the Gravity Gloves' beam: a single byte when there's none
            // (bits 0-1: the mode, bit 2: there's something V would merge with; bit 3, which isn't the beam's: the
            // flashlight is on, 1.05; bits 4 and 5, nor are they: in the air, and in a plane, 1.06, the height after it;
            // bit 6: the chute braked, bit 7: climbing a ladder or a hatch, 1.09)
            const mode = beam?.mode ?? GravityBeamMode.Off;
            const merge = mode === GravityBeamMode.Holding ? beam?.merge : undefined;
            // (0.98.4: tossed by a tornado, both the plane's and the brake's bits: never both otherwise)
            const tossed = air?.tossed === true;
            stream.writeUint8(mode | (merge !== undefined ? 4 : 0) | (flashlight ? 8 : 0) | (air ? 16 : 0) | (air?.aboard || tossed ? 32 : 0) | (air?.braking || tossed ? 64 : 0) | (climbing ? 128 : 0));
            if (beam !== undefined && mode !== GravityBeamMode.Off) {
                stream.writeFloat(Math.min(Math.max(beam.length, 0), 64), 0, 64, 1);
                if (mode !== GravityBeamMode.Reaching) stream.writeObjectId(beam.target ?? 0);
                if (merge !== undefined) stream.writeObjectId(merge);
            }
            if (air) stream.writeUint8(heightToByte(air.height));
            // the Octo Gadget's arms (1.10): a byte saying whether there are any, then each arm's state, the direction of
            // its claw from the player and how far out it is (21 bytes for a player who carries the gadget)
            stream.writeUint8(octo === undefined ? 0 : 1);
            if (octo !== undefined) {
                for (const arm of octo) {
                    stream.writeUint8(arm.state & 3);
                    stream.writeRotation2(arm.angle);
                    stream.writeFloat(Numeric.clamp(arm.distance, 0, OctoRig.wire), 0, OctoRig.wire, 2);
                }
            }
        },
        serializeFull(
            stream,
            { full: {
                layer,
                dead,
                downed,
                beingRevived,
                teamID,
                invulnerable,
                activeItem,
                sizeMod,
                reloadMod,
                skin,
                helmet,
                vest,
                mask,
                backpack,
                halloweenThrowableSkin,
                activeDisguise,
                infected,
                backEquippedMelee,
                hasBubble,
                activeOverdrive,
                hasMagneticField,
                isCycling,
                emitLowHealthParticles,
                emblem,
                kills,
                storeEmblem
            } }
        ): void {
            stream.writeLayer(layer);
            const hasReloadMod = reloadMod !== undefined;
            const hasHelmet = helmet !== undefined;
            const hasVest = vest !== undefined;
            const hasDisguise = activeDisguise !== undefined;
            const hasBackEquippedMelee = backEquippedMelee !== undefined;

            stream.writeBooleanGroup2(
                dead,
                downed,
                beingRevived,
                invulnerable,
                hasReloadMod,
                halloweenThrowableSkin,
                hasHelmet,
                hasVest,
                hasDisguise,
                infected,
                hasBackEquippedMelee,
                hasBubble,
                activeOverdrive,
                hasMagneticField,
                isCycling,
                emitLowHealthParticles
            );
            stream.writeUint8(teamID);
            Loots.writeToStream(stream, activeItem);

            stream.writeFloat(sizeMod, 0, 4, 1);

            if (hasReloadMod) {
                stream.writeFloat(reloadMod, 0, 4, 1);
            }

            Skins.writeToStream(stream, skin);

            if (hasHelmet) Armors.writeToStream(stream, helmet);
            if (hasVest) Armors.writeToStream(stream, vest);
            stream.writeUint8(mask !== undefined ? 1 : 0);
            if (mask !== undefined) Armors.writeToStream(stream, mask);
            Backpacks.writeToStream(stream, backpack);

            if (hasDisguise) Obstacles.writeToStream(stream, activeDisguise);
            if (hasBackEquippedMelee) Melees.writeToStream(stream, backEquippedMelee);

            writeEmblem(stream, emblem);
            stream.writeUint8(Math.max(0, Math.min(255, kills | 0)));
            // (an empty string, one byte, when it wears none: a store item's id is 32 characters at most)
            stream.writeString(32, storeEmblem ?? "");
        },
        deserializePartial(stream) {
            const data: Mutable<ObjectsNetData[ObjectCategory.Player]> = {
                position: stream.readPosition(),
                rotation: stream.readRotation2()
            };

            // see serialization comment
            const actAnim = stream.readUint8();
            const hasAnimation = (actAnim & 128) !== 0;
            const hasAction = (actAnim & 1) !== 0;

            data.animation = hasAnimation ? (actAnim >> 3) & 15 : undefined;

            const action: PlayerActions | undefined = hasAction ? (actAnim >> 1) & 3 : undefined;
            if (action !== undefined) {
                const act = {
                    type: action
                } as Mutable<NonNullable<ObjectsNetData[ObjectCategory.Player]["action"]>>;

                if (action === PlayerActions.UseItem) {
                    act.item = Loots.readFromStream<HealingItemDefinition>(stream);
                }

                data.action = act;
            }

            const beamByte = stream.readUint8();
            data.flashlight = (beamByte & 8) !== 0;
            const mode = (beamByte & 3) as GravityBeamMode;
            if (mode !== GravityBeamMode.Off) {
                const length = stream.readFloat(0, 64, 1);
                const target = mode !== GravityBeamMode.Reaching ? stream.readObjectId() : undefined;
                const merge = (beamByte & 4) !== 0 ? stream.readObjectId() : undefined;
                data.beam = { mode, length, target, merge };
            }
            if ((beamByte & 16) !== 0) {
                const tossed = (beamByte & 32) !== 0 && (beamByte & 64) !== 0;
                data.air = { height: heightFromByte(stream.readUint8()), aboard: !tossed && (beamByte & 32) !== 0, braking: !tossed && (beamByte & 64) !== 0, tossed };
            }
            data.climbing = (beamByte & 128) !== 0;
            if ((stream.readUint8() & 1) !== 0) {
                const arms: OctoArmData[] = [];
                for (let i = 0; i < OctoRig.arms; i++) {
                    const state = (stream.readUint8() & 3) as OctoArmState;
                    const angle = stream.readRotation2();
                    const distance = stream.readFloat(0, OctoRig.wire, 2);
                    arms.push({ state, angle, distance });
                }
                data.octo = arms;
            }

            return data;
        },
        deserializeFull(stream) {
            const layer = stream.readLayer();
            const [
                dead,
                downed,
                beingRevived,
                invulnerable,
                hasReloadMod,
                halloweenThrowableSkin,
                hasHelmet,
                hasVest,
                hasDisguise,
                infected,
                hasBackEquippedMelee,
                hasBubble,
                activeOverdrive,
                hasMagneticField,
                isCycling,
                emitLowHealthParticles
            ] = stream.readBooleanGroup2();

            return {
                layer,
                dead,
                downed,
                beingRevived,
                invulnerable,
                halloweenThrowableSkin,
                teamID: stream.readUint8(),
                activeItem: Loots.readFromStream(stream),
                sizeMod: stream.readFloat(0, 4, 1),
                reloadMod: hasReloadMod ? stream.readFloat(0, 4, 1) : undefined,
                skin: Skins.readFromStream(stream),
                helmet: hasHelmet ? Armors.readFromStream(stream) : undefined,
                vest: hasVest ? Armors.readFromStream(stream) : undefined,
                mask: stream.readUint8() === 1 ? Armors.readFromStream(stream) : undefined,
                backpack: Backpacks.readFromStream(stream),
                activeDisguise: hasDisguise ? Obstacles.readFromStream(stream) : undefined,
                infected,
                backEquippedMelee: hasBackEquippedMelee ? Melees.readFromStream(stream) : undefined,
                hasBubble,
                activeOverdrive,
                hasMagneticField,
                isCycling,
                emitLowHealthParticles,
                emblem: readEmblem(stream),
                kills: stream.readUint8(),
                storeEmblem: stream.readString(32) || undefined
            };
        }
    },
    //
    // Obstacle Serialization
    //
    [ObjectCategory.Obstacle]: {
        serializePartial(stream, data): void {
            const dynamic = data.dynamic;
            stream.writeBooleanGroup(
                data.dead,
                data.playMaterialDestroyedSound,
                data.waterOverlay,
                data.powered,
                data.building ?? false,
                ((data.damageStage ?? 0) & 1) !== 0,
                ((data.damageStage ?? 0) & 2) !== 0,
                dynamic !== undefined
            );
            stream.writeScale(data.scale);
            if (dynamic !== undefined) {
                stream.writePosition(dynamic.position);
                stream.writeRotation(Angle.normalize(dynamic.spin));
                stream.writeUint16(dynamic.bond);
            }
        },
        serializeFull(
            stream,
            {
                full: {
                    position,
                    definition,
                    rotation,
                    door,
                    activated,
                    layer
                }
            }
        ): void {
            Obstacles.writeToStream(stream, definition);

            stream.writePosition(position);
            stream.writeLayer(layer);

            /*
                here we're condensing stuff to try and minimize deadspace
                this is really peak tryharding
                we have:
                - a rotation
                possibly one of:
                    - door stuff
                    - activation stuff
            */

            let obstacleData = 0;

            if (definition.isDoor && door) {
                // 3 bits
                obstacleData += door.offset * 2 + (door.locked ? 1 : 0);
                //                            ^ shift left by one

                // will result in something like 0110 0101
                // or more generally, xxx00xxx
            } else if (definition.isActivatable) {
                // 1 bit
                obstacleData += activated ? 1 : 0;
                // will result in something like xxx0000x
            }

            /*
                what remains is the door/activation/detector stuff
                door stuff is 3 bits, the other two are 1 bit
                thus, we conclude that obstacleData will never exceed 3 bits

                RotationMode.Full takes a clean 2 bytes, so it's not of a concern
                Limited and Binary take 2 and 1 respectively

                thus we see that if the mode is limited or binary, we can fit
                the rotation and the data in a single 8-bit number

                for example, with rotation 2 and door offset of 2 and locked

                we get
                0 0 0 1 0 1 0 1
                      |_| | |
                       |  | locked
                    rotation
                          offset
            */

            switch (definition.rotationMode) {
                case RotationMode.Full: {
                    // rotation doesn't leave any deadspace, so we write
                    // it and the data

                    // to make deserialization easier though, always write
                    // the obstacle data first
                    stream.writeUint8(obstacleData);
                    stream.writeRotation(rotation.rotation);
                    break;
                }
                case RotationMode.Limited:
                case RotationMode.Binary: {
                    stream.writeUint8(obstacleData + (rotation.rotation << 3));
                    // shift into correct position with a << 3
                    break;
                }
                case RotationMode.None: {
                    // there may be no rotation data, but there's still obstacle data and
                    // all the other thingies
                    stream.writeUint8(obstacleData);
                    break;
                }
            }
        },
        deserializePartial(stream) {
            const [
                dead,
                playMaterialDestroyedSound,
                waterOverlay,
                powered,
                building,
                damageLow,
                damageHigh,
                dynamic
            ] = stream.readBooleanGroup();

            return {
                scale: stream.readScale(),
                dead,
                playMaterialDestroyedSound,
                waterOverlay,
                building,
                damageStage: (damageLow ? 1 : 0) + (damageHigh ? 2 : 0),
                powered,
                dynamic: dynamic
                    ? {
                        position: stream.readPosition(),
                        spin: stream.readRotation(),
                        bond: stream.readUint16()
                    }
                    : undefined
            };
        },
        deserializeFull(stream) {
            const definition = Obstacles.readFromStream(stream);

            const data: SDeepMutable<NonNullable<ObjectsNetData[ObjectCategory.Obstacle]["full"]>> = {
                definition,
                position: stream.readPosition(),
                layer: stream.readLayer(),
                rotation: {
                    orientation: 0,
                    rotation: 0
                }
            };

            // see the comments in serializeFull to understand what's going on
            // "safe" version
            /*
            switch (definition.rotationMode) {
                case RotationMode.Full: {
                    data.rotation.rotation = stream.readRotation();
                    break;
                }
                case RotationMode.Limited:
                case RotationMode.Binary: {
                    data.rotation.orientation = stread.readUint8() as Orientation;
                    data.rotation.rotation = definition.rotationMode === RotationMode.Binary
                        ? orientation * halfπ // sus
                        : -Angle.normalize(orientation) * halfπ;
                    break;
                }
                // case RotationMode.None: {
                //     break;
                // }
            }

            if (definition.isDoor && door) {
                const door = stream.readUint8();
                data.door = {
                    offset: (door >> 1) & 3,
                    locked: (door & 1) === 1
                };
            } else if (definition.isActivatable) {
                data.activated = stream.readUint8() !== 0;
            }
            */
            const obstacleData = stream.readUint8();

            if (definition.isDoor) {
                data.door = {
                    offset: (obstacleData >> 1) & 3,
                    locked: (obstacleData & 1) === 1
                };
            } else if (definition.isActivatable) {
                data.activated = (obstacleData & 1) === 1;
            }

            switch (definition.rotationMode) {
                case RotationMode.Full: {
                    data.rotation.rotation = stream.readRotation();
                    break;
                }
                case RotationMode.Limited:
                case RotationMode.Binary: {
                    const orientation = (obstacleData & 0b11000) / 8 as Orientation;

                    data.rotation.orientation = orientation;
                    data.rotation.rotation = definition.rotationMode === RotationMode.Binary
                        ? orientation * halfπ // sus
                        : -Angle.normalize(orientation) * halfπ;
                    break;
                }
                // case RotationMode.None: {
                //     break;
                // }
            }
            return data;
        }
    },
    //
    // Loot Serialization
    //
    [ObjectCategory.Loot]: {
        serializePartial(stream, data): void {
            stream.writePosition(data.position);
            stream.writeLayer(data.layer);
        },
        serializeFull(stream, { full }): void {
            Loots.writeToStream(stream, full.definition);
            /*
                package 'isNew' and 'count' in a single 16-bit
                integer—the first bit will be for isNew, the other
                15 will be for the count
            */
            stream.writeUint16(
                (full.isNew ? 32768 : 0) + full.count
                //            ^^^^^ 1 << 15
            );
        },
        deserializePartial(stream) {
            return {
                position: stream.readPosition(),
                layer: stream.readLayer()
            };
        },
        deserializeFull(stream) {
            const definition = Loots.readFromStream(stream);
            const amount = stream.readUint16();
            return {
                definition,
                count: amount & 32767, // mask out the MSB
                isNew: (amount & 32768) !== 0 // extract the MSB
            };
        }
    },
    //
    // Death Marker Serialization
    //
    [ObjectCategory.DeathMarker]: {
        serializePartial(stream, data): void {
            stream.writePosition(data.position);
            stream.writeLayer(data.layer);
            // (one byte: whether it's new, whether its body has a pool, and its age in steps: 1.06.1)
            const steps = Math.max(0, Math.min(DEATH_AGE_STEPS, Math.floor((data.age ?? 0) / DEATH_AGE_STEP)));
            stream.writeUint8((data.isNew ? 0x80 : 0) | (data.pool ? 0x40 : 0) | steps);
            stream.writeObjectId(data.playerID);
        },
        serializeFull(): void { /* death markers have no full serialization */ },
        deserializePartial(stream) {
            const position = stream.readPosition();
            const layer = stream.readLayer();
            const byte = stream.readUint8();
            return {
                position,
                layer,
                isNew: (byte & 0x80) !== 0,
                pool: (byte & 0x40) !== 0,
                age: (byte & DEATH_AGE_STEPS) * DEATH_AGE_STEP,
                playerID: stream.readObjectId()
            };
        },
        deserializeFull() { /* death markers have no full serialization */ }
    },
    //
    // Building Serialization
    //
    [ObjectCategory.Building]: {
        serializePartial(stream, data): void {
            stream.writeBooleanGroup(
                data.dead,
                data.puzzle !== undefined,
                // for now, this is okay, since the space isn't being used
                // up anyways—if space is needed in the future, then
                // these two booleans can be booted off
                data.puzzle?.solved,
                data.puzzle?.errorSeq,
                // (1.08: its roof's glass, written only when a pane is smashed)
                data.brokenGlass !== 0,
                // (0.98.4: its roof torn off by a tornado)
                (data.roof ?? 0) !== 0
            );
            stream.writeLayer(data.layer);
            if (data.brokenGlass !== 0) stream.writeUint16(data.brokenGlass);
        },
        serializeFull(stream, { full }): void {
            Buildings.writeToStream(stream, full.definition);
            stream.writePosition(full.position);
            stream.writeUint8(full.orientation);
        },
        deserializePartial(stream) {
            const [
                dead,
                hasPuzzle,
                solved,
                errorSeq,
                hasBrokenGlass,
                roofTorn
            ] = stream.readBooleanGroup();
            const layer = stream.readLayer();

            return {
                dead,
                puzzle: hasPuzzle
                    ? { solved, errorSeq }
                    : undefined,
                layer,
                brokenGlass: hasBrokenGlass ? stream.readUint16() : 0,
                roof: roofTorn ? 1 : 0
            };
        },
        deserializeFull(stream) {
            return {
                definition: Buildings.readFromStream(stream),
                position: stream.readPosition(),
                orientation: stream.readUint8() as Orientation
            };
        }
    },
    //
    // Decal Serialization
    //
    [ObjectCategory.Decal]: {
        serializePartial(stream, data): void {
            Decals.writeToStream(stream, data.definition);
            stream.writePosition(data.position);
            stream.writeObstacleRotation(data.rotation, data.definition.rotationMode);
            stream.writeLayer(data.layer);
            // a trail's mark: its age (whole seconds, at most 255) and strength (a byte)
            if (data.definition.trail) {
                const age = data.trail?.age ?? 0;
                const strength = data.trail?.strength ?? 1;
                stream.writeUint8(Math.min(255, Math.max(0, Math.floor(Number.isFinite(age) ? age : 0))));
                stream.writeUint8(Math.min(255, Math.max(0, Math.round((Number.isFinite(strength) ? strength : 1) * 255))));
            }
        },
        serializeFull(): void { /* decals have no full serialization */ },
        deserializePartial(stream) {
            const definition = Decals.readFromStream(stream);
            const position = stream.readPosition();
            const rotation = stream.readObstacleRotation(definition.rotationMode).rotation;
            const layer = stream.readLayer();
            return {
                definition,
                position,
                rotation,
                layer,
                ...(definition.trail ? { trail: { age: stream.readUint8(), strength: stream.readUint8() / 255 } } : {})
            };
        },
        deserializeFull() { /* decals have no full serialization */ }
    },
    [ObjectCategory.Parachute]: {
        serializePartial(stream, data) {
            stream.writeFloat(data.height, 0, 1, 1);
        },
        serializeFull(stream, { full }) {
            stream.writePosition(full.position);
        },
        deserializePartial(stream) {
            return {
                height: stream.readFloat(0, 1, 1)
            };
        },
        deserializeFull(stream) {
            return {

                position: stream.readPosition()

            };
        }
    },
    [ObjectCategory.SyncedParticle]: {
        serializePartial(stream, data) {
            const {
                definition,
                startPosition,
                endPosition,
                layer,
                age,
                lifetime,
                angularVelocity,
                scale,
                alpha,
                variant,
                creatorID
            } = data;

            SyncedParticles.writeToStream(stream, definition);
            stream.writePosition(startPosition);
            stream.writePosition(endPosition);
            stream.writeLayer(layer);
            stream.writeFloat(age, 0, 1, 1);

            if (typeof definition.lifetime === "object") {
                const { min, max } = definition.lifetime;
                // biome-ignore lint/style/noNonNullAssertion: cannot be undefined here
                stream.writeFloat(lifetime!, min, max, 1);
            }

            if (typeof definition.angularVelocity === "object") {
                const { min, max } = definition.angularVelocity;
                // biome-ignore lint/style/noNonNullAssertion: cannot be undefined here
                stream.writeFloat(angularVelocity!, min, max, 1);
            }

            if (scale !== undefined) {
                stream.writeScale(scale.start);
                stream.writeScale(scale.end);
            }

            if (alpha !== undefined) {
                stream.writeFloat(alpha.start, 0, 1, 1);
                stream.writeFloat(alpha.end, 0, 1, 1);
            }

            if (definition.variations !== undefined && variant !== undefined) {
                stream.writeUint8(variant);
            }

            if (creatorID !== undefined) {
                stream.writeObjectId(creatorID);
            }
        },
        serializeFull() { /* no full serialization */ },
        deserializePartial(stream) {
            const data: Mutable<ObjectsNetData[ObjectCategory.SyncedParticle]> = {
                definition: SyncedParticles.readFromStream(stream),
                startPosition: stream.readPosition(),
                endPosition: stream.readPosition(),
                layer: stream.readLayer(),
                age: stream.readFloat(0, 1, 1)
            };

            const {
                lifetime,
                angularVelocity,
                scale,
                alpha,
                variations,
                hasCreatorID
            } = data.definition;

            if (typeof lifetime === "object") {
                data.lifetime = stream.readFloat(lifetime.min, lifetime.max, 1);
            }

            if (typeof angularVelocity === "object") {
                data.angularVelocity = stream.readFloat(angularVelocity.min, angularVelocity.max, 1);
            }

            if (typeof scale === "object") {
                data.scale = {
                    start: stream.readScale(),
                    end: stream.readScale()
                };
            }

            if (typeof alpha === "object") {
                data.alpha = {
                    start: stream.readFloat(0, 1, 1),
                    end: stream.readFloat(0, 1, 1)
                };
            }

            if (variations !== undefined) {
                data.variant = stream.readUint8() as Variation;
            }

            if (hasCreatorID) {
                data.creatorID = stream.readObjectId();
            }

            return data;
        },
        deserializeFull() { /* no full serialization */ }
    },
    [ObjectCategory.Projectile]: {
        serializePartial(strm, data) {
            strm.writePosition(data.position)
                .writeRotation2(data.rotation)
                .writeLayer(data.layer)
                .writeFloat(data.height, 0, GameConstants.projectiles.maxHeight, 1);
        },
        serializeFull(stream, { full }) {
            Throwables.writeToStream(stream, full.definition);
            stream.writeBooleanGroup(full.halloweenSkin, full.activated);

            if (full.definition.c4 && full.c4) {
                stream.writeUint8(full.c4.throwerTeamID);
                stream.writeUint8(full.c4.tintIndex);
            }
        },
        deserializePartial(stream) {
            return {
                position: stream.readPosition(),
                rotation: stream.readRotation2(),
                layer: stream.readLayer(),
                height: stream.readFloat(0, GameConstants.projectiles.maxHeight, 1)
            };
        },
        deserializeFull(stream) {
            const definition = Throwables.readFromStream(stream);
            const [halloweenSkin, activated] = stream.readBooleanGroup();

            const data: Mutable<ObjectsNetData[ObjectCategory.Projectile]["full"]> = {
                definition,
                halloweenSkin,
                activated
            };

            if (definition.c4) {
                data.c4 = {
                    throwerTeamID: stream.readUint8(),
                    tintIndex: stream.readUint8()
                };
            }

            return data;
        }
    },
    //
    // Animal Serialization
    //
    [ObjectCategory.Animal]: {
        serializePartial(stream, data): void {
            stream.writePosition(data.position);
            stream.writeRotation(data.rotation);
            stream.writeLayer(data.layer);
            stream.writeUint8(data.state);
            stream.writeUint8(data.kind);
            stream.writeUint8(animalHeightByte(data.height));
        },
        serializeFull(): void { /* hares carry everything in the partial update */ },
        deserializePartial(stream) {
            return {
                position: stream.readPosition(),
                rotation: stream.readRotation(),
                layer: stream.readLayer(),
                state: stream.readUint8(),
                kind: stream.readUint8(),
                height: animalHeightFromByte(stream.readUint8())
            };
        },
        deserializeFull() { /* hares carry everything in the partial update */ }
    },
    //
    // Fire Serialization
    //
    [ObjectCategory.Fire]: {
        serializePartial(stream, data): void {
            stream.writeUint16(data.chunkX);
            stream.writeUint16(data.chunkY);
            for (const mask of [data.burning, data.kindling, data.fuel, data.scorched]) {
                stream.writeUint32(mask.lo >>> 0);
                stream.writeUint32(mask.hi >>> 0);
            }
        },
        serializeFull(): void { /* fire chunks carry everything in the partial update */ },
        deserializePartial(stream) {
            const chunkX = stream.readUint16();
            const chunkY = stream.readUint16();
            const read = (): FireMask => ({ lo: stream.readUint32(), hi: stream.readUint32() });
            return { chunkX, chunkY, burning: read(), kindling: read(), fuel: read(), scorched: read() };
        },
        deserializeFull() { /* fire chunks carry everything in the partial update */ }
    }
};
